import { useEffect, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { motion } from 'framer-motion';
import { CommunitySearch } from './CommunitySearch';
import { SiteMenu } from './SiteMenu';
import { NavLogo } from './NavLogo';
import { plainText } from './propertyUtils';

const LOGO_DEFAULT = '/american-landmark-logo-white.png';
const SEARCH_TYPING_PHRASES = [
  'pet friendly apartments in tampa',
  '2 bedroom under $1800 in austin',
  '1 bedroom apartment in orlando fl',
];

const liveBgDef: SlotGroupDefinition = {
  label: 'Live Background',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Live background',
    },
  },
};

const investBgDef: SlotGroupDefinition = {
  label: 'Invest Background',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Invest background',
    },
  },
};

const thriveBgDef: SlotGroupDefinition = {
  label: 'Thrive Background',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Thrive background',
    },
  },
};

const NAV_LINKS = [
  { href: '/our-communities', label: 'OUR COMMUNITIES' },
  { href: '/investors', label: 'INVEST WITH US', sora: true },
  { href: '/careers', label: 'WORK WITH US' },
];

export function HomeHero() {
  const [active, setActive] = useState(0);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const searchPlaceholder = plainText(useContentToken('hero.search_placeholder'));
  const ctaText = useContentToken('hero.cta_text');
  const logo = useContentToken('hero.logo');
  const logoAlt = plainText(useContentToken('hero.logo_alt'));
  const menuBackground = useContentToken('hero.menu_background');
  const liveBg = useSlotGroup('hero.bg_live', liveBgDef);
  const investBg = useSlotGroup('hero.bg_invest', investBgDef);
  const thriveBg = useSlotGroup('hero.bg_thrive', thriveBgDef);

  const logoUrl = logo || LOGO_DEFAULT;
  const menuTrigger = (
    <button
      type="button"
      className={`inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full transition lg:h-[60px] lg:w-[60px] ${
        scrolled
          ? 'bg-[rgb(var(--ala-primary))]/10 text-[rgb(var(--ala-primary))] hover:bg-[rgb(var(--ala-primary))]/20'
          : 'bg-[rgb(var(--ala-on-primary))]/10 text-[rgb(var(--ala-on-primary))] hover:bg-[rgb(var(--ala-on-primary))]/20'
      }`}
      aria-label="Open menu"
    >
      <span className="flex flex-col gap-1.5" aria-hidden>
        <span className="block h-0.5 w-5 bg-current lg:w-6" />
        <span className="block h-0.5 w-5 bg-current lg:w-6" />
        <span className="block h-0.5 w-5 bg-current lg:w-6" />
      </span>
    </button>
  );
  const liveImage =
    liveBg.image ||
    '';
  const investImage =
    investBg.image ||
    '';
  const thriveImage =
    thriveBg.image ||
    '';

  return (
    <section className="relative flex h-[78svh] min-h-[580px] w-full md:h-[100svh] md:min-h-[100svh] flex-col items-center justify-start overflow-hidden px-4 md:px-8 lg:px-12">
      <div className="absolute inset-0 z-0 overflow-hidden">
        <EditableSlotGroup groupPath="hero.bg_live" definition={liveBgDef}>
          <motion.div
            className="absolute inset-0 overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: active === 0 ? 1 : 0 }}
            transition={{ duration: 0.9, ease: 'easeInOut' }}
          >
            <motion.div
              className="h-full w-full"
              animate={active === 0 ? { scale: [1, 1.12], x: [0, -24] } : { scale: 1, x: 0 }}
              transition={
                active === 0
                  ? { duration: 18, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }
                  : { duration: 0.9 }
              }
            >
              <VibesitesImage
                url={liveImage}
                alt={liveBg.alt || 'Live background'}
                priority
                sizes="100vw"
                className="h-full w-full object-cover"
              />
            </motion.div>
          </motion.div>
        </EditableSlotGroup>

        <EditableSlotGroup groupPath="hero.bg_invest" definition={investBgDef}>
          <motion.div
            className="absolute inset-0 overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: active === 1 ? 1 : 0 }}
            transition={{ duration: 0.9, ease: 'easeInOut' }}
          >
            <motion.div
              className="h-full w-full"
              animate={active === 1 ? { scale: [1, 1.12], x: [0, -24] } : { scale: 1, x: 0 }}
              transition={
                active === 1
                  ? { duration: 18, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }
                  : { duration: 0.9 }
              }
            >
              <VibesitesImage
                url={investImage}
                alt={investBg.alt || 'Invest background'}
                sizes="100vw"
                className="h-full w-full object-cover"
              />
            </motion.div>
          </motion.div>
        </EditableSlotGroup>

        <EditableSlotGroup groupPath="hero.bg_thrive" definition={thriveBgDef}>
          <motion.div
            className="absolute inset-0 overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: active === 2 ? 1 : 0 }}
            transition={{ duration: 0.9, ease: 'easeInOut' }}
          >
            <motion.div
              className="h-full w-full"
              animate={active === 2 ? { scale: [1, 1.12], x: [0, -24] } : { scale: 1, x: 0 }}
              transition={
                active === 2
                  ? { duration: 18, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }
                  : { duration: 0.9 }
              }
            >
              <VibesitesImage
                url={thriveImage}
                alt={thriveBg.alt || 'Thrive background'}
                sizes="100vw"
                className="h-full w-full object-cover"
              />
            </motion.div>
          </motion.div>
        </EditableSlotGroup>

        <div className="absolute inset-0 bg-black/[0.34]" />
        <div className="absolute inset-x-0 top-0 h-52 bg-gradient-to-b from-black/55 to-transparent" />
      </div>

      <div className="relative z-30 mx-auto flex h-full w-full max-w-[1440px] flex-col justify-between pt-12">
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          // top-14 sits directly under the Header's pinned utility bar (py-2 + h-10 icons).
          className={`fixed inset-x-0 top-14 z-40 transition-[background-color,box-shadow] duration-500 ease-out ${
            scrolled ? 'bg-[#ECECEC] shadow-lg' : 'bg-[#ECECEC]/0 shadow-none'
          }`}
        >
        <div
          className={`mx-auto flex w-full max-w-[1440px] items-center justify-between gap-4 px-4 transition-[padding] duration-500 ease-out md:px-8 lg:px-12 ${
            scrolled ? 'py-2' : 'py-3'
          }`}
        >
          <a href="/" className="shrink-0">
            <EditableToken path="hero.logo" type="image" as="div" label="Hero Logo">
              <NavLogo url={logoUrl} alt={logoAlt || 'American Landmark'} docked={scrolled} />
            </EditableToken>
          </a>
          <div className="sr-only">
            <RichTextContent path="hero.logo_alt" label="Hero Logo Alt" as="span" />
            <EditableToken path="hero.menu_background" type="image" as="div" label="Menu Background">
              <span>Menu Background</span>
            </EditableToken>
          </div>

          <nav className="hidden min-w-0 flex-1 items-center justify-end gap-4 lg:flex lg:gap-8">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`whitespace-nowrap text-[18px] uppercase tracking-[0.3px] transition-colors hover:text-[rgb(var(--ala-accent))] ${
                  scrolled
                    ? 'text-[rgb(var(--ala-primary))]'
                    : 'text-[rgb(var(--ala-on-primary))] [text-shadow:0_1px_3px_rgba(0,0,0,0.55)]'
                } ${link.sora ? 'font-sora font-medium' : 'font-heading font-medium'}`}
              >
                {link.label}
              </a>
            ))}
            <SiteMenu
              logoUrl={logoUrl}
              logoAlt={logoAlt || 'American Landmark'}
              backgroundUrl={menuBackground}
              trigger={menuTrigger}
            />
          </nav>

          <div className="lg:hidden">
            <SiteMenu
              logoUrl={logoUrl}
              logoAlt={logoAlt || 'American Landmark'}
              backgroundUrl={menuBackground}
              trigger={menuTrigger}
            />
          </div>
        </div>
        </motion.div>

        <div className="flex w-full flex-1 flex-col items-center pb-6 md:pb-12">
          <div className="flex-1" />
          <div className="w-full max-w-xl px-4 md:absolute md:left-1/2 md:top-1/2 md:z-10 md:-translate-x-1/2 md:-translate-y-1/2">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="w-full"
          >
            <div className="sr-only">
              <RichTextContent path="hero.search_placeholder" label="Search Placeholder" as="span" />
            </div>
            <CommunitySearch
              placeholder={searchPlaceholder || SEARCH_TYPING_PHRASES[0]}
              typingPlaceholders={SEARCH_TYPING_PHRASES}
              buttonText={
                <EditableToken path="hero.cta_text" label="Search Button Text">
                  <span className="sr-only">{ctaText || 'Search'}</span>
                </EditableToken>
              }
              variant="hero"
            />
            <RichTextContent
              path="hero.search_hint"
              label="Search Hint"
              as="p"
              className="mt-3 text-center font-heading text-sm font-semibold tracking-wide text-[rgb(var(--ala-on-primary))] [text-shadow:0_1px_4px_rgba(0,0,0,0.45)]"
            />
          </motion.div>
          </div>
          <div className="flex-1" />

          <div className="flex w-full flex-col items-start justify-start gap-4 px-2 [container-type:inline-size] md:flex-row md:items-end md:justify-between md:gap-0 md:px-6">
            <motion.button
              type="button"
              initial={{ y: 80, opacity: 0, filter: 'blur(10px)' }}
              animate={{ y: 0, opacity: 1, filter: 'blur(0px)' }}
              transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onMouseEnter={() => setActive(0)}
              onClick={() => setActive(0)}
              className={`cursor-pointer text-left font-heading text-[clamp(2.75rem,12vw,4.5rem)] font-bold leading-[1.1] tracking-[-0.0758em] transition-colors duration-300 md:text-[length:clamp(56px,13cqw,230px)] ${
                active === 0 ? 'text-[rgb(var(--ala-accent))]' : 'text-[rgb(var(--ala-on-primary))] hover:text-[rgb(var(--ala-accent))]'
              }`}
            >
              <RichTextContent path="hero.line_one" label="Headline Line 1" as="span" />
            </motion.button>
            <motion.button
              type="button"
              initial={{ y: 80, opacity: 0, filter: 'blur(10px)' }}
              animate={{ y: 0, opacity: 1, filter: 'blur(0px)' }}
              transition={{ duration: 0.9, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
              onMouseEnter={() => setActive(1)}
              onClick={() => setActive(1)}
              className={`cursor-pointer text-left font-heading text-[clamp(2.75rem,12vw,4.5rem)] font-bold leading-[1.1] tracking-[-0.0758em] transition-colors duration-300 md:text-[length:clamp(56px,13cqw,230px)] ${
                active === 1 ? 'text-[rgb(var(--ala-accent))]' : 'text-[rgb(var(--ala-on-primary))] hover:text-[rgb(var(--ala-accent))]'
              }`}
            >
              <RichTextContent path="hero.line_two" label="Headline Line 2" as="span" />
            </motion.button>
            <motion.button
              type="button"
              initial={{ y: 80, opacity: 0, filter: 'blur(10px)' }}
              animate={{ y: 0, opacity: 1, filter: 'blur(0px)' }}
              transition={{ duration: 0.9, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
              onMouseEnter={() => setActive(2)}
              onClick={() => setActive(2)}
              className={`cursor-pointer text-left font-heading text-[clamp(2.75rem,12vw,4.5rem)] font-bold leading-[1.1] tracking-[-0.0758em] transition-colors duration-300 md:text-[length:clamp(56px,13cqw,230px)] ${
                active === 2 ? 'text-[rgb(var(--ala-accent))]' : 'text-[rgb(var(--ala-on-primary))] hover:text-[rgb(var(--ala-accent))]'
              }`}
            >
              <RichTextContent path="hero.line_three" label="Headline Line 3" as="span" />
            </motion.button>
          </div>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Home Hero',
  tokens: {
    'hero.logo': {
      label: 'Hero Logo',
      type: 'image',
      defaultValue: '/american-landmark-logo-white.png',
    },
    'hero.menu_background': {
      label: 'Menu Background',
      type: 'image',
      defaultValue:
        'https://media.multihub.io/cdn-cgi/image/format=auto,w=1280/company/5695708787113984/01a0f3b3-4638-77ec-931c-faa1b0b98304.webp',
    },
    'hero.logo_alt': {
      label: 'Hero Logo Alt',
      type: 'richtext',
      defaultField: 'property_company_name',
      defaultValue: '<p>American Landmark</p>',
    },
    'hero.line_one': {
      label: 'Headline Line 1',
      type: 'richtext',
      defaultValue: '<p>Live</p>',
    },
    'hero.line_two': {
      label: 'Headline Line 2',
      type: 'richtext',
      defaultValue: '<p>Invest</p>',
    },
    'hero.line_three': {
      label: 'Headline Line 3',
      type: 'richtext',
      defaultValue: '<p>Thrive</p>',
    },
    'hero.search_hint': {
      label: 'Search Hint',
      type: 'richtext',
      defaultValue: '<p>Powered by AI · Searching 100+ ALA communities</p>',
    },
    'hero.search_placeholder': {
      label: 'Search Placeholder',
      type: 'richtext',
      defaultValue: '<p>1 bedroom apartment in tampa fl</p>',
    },
    'hero.cta_text': {
      label: 'Search Button Text',
      type: 'text',
      defaultValue: 'Search',
    },
  },
  slotGroups: {
    'hero.bg_live': liveBgDef,
    'hero.bg_invest': investBgDef,
    'hero.bg_thrive': thriveBgDef,
  },
};

(HomeHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HomeHero;
